import { nanoid } from "nanoid";
import type { AnalysisScopeMentionInput } from "@/lib/ai/analysis-scope";
import {
  type AnalysisTarget,
  formatAnalysisTargetLabel,
  getAnalysisTargetKey,
} from "@/lib/ai/analysis-target";

export type AnalysisTargetMentionInstance = {
  mentionId: string;
  refId: string;
  target: AnalysisTarget;
  start: number;
  end: number;
};

export type AnalysisTargetMentionTokenRange = AnalysisTargetMentionInstance;

export type AnalysisTargetHighlightSegment =
  | {
      kind: "plain";
      text: string;
    }
  | {
      kind: "mention";
      text: string;
      mention: AnalysisTargetMentionInstance;
      target: AnalysisTarget;
    };

type MentionTextChange = {
  previousStart: number;
  previousEnd: number;
  delta: number;
};

/**
 * target を textarea 内へ挿入する標準表記へ変換する。
 */
export function getAnalysisTargetMentionText(target: AnalysisTarget): string {
  return `@${formatAnalysisTargetLabel(target)}`;
}

/**
 * メンション選択時に保存する instance を生成する。
 *
 * 契約:
 * - `refId` は選択時点の target identity を固定する。
 * - `start/end` は textarea の生文字列上の範囲を指す。
 */
export function createAnalysisTargetMentionInstance(
  target: AnalysisTarget,
  start: number,
  end: number,
): AnalysisTargetMentionInstance {
  return {
    mentionId: nanoid(8),
    refId: getAnalysisTargetKey(target),
    target,
    start,
    end,
  };
}

/**
 * mention instances を範囲順へ正規化する。
 */
export function getAnalysisTargetMentionTokenRanges(
  mentions: AnalysisTargetMentionInstance[],
): AnalysisTargetMentionTokenRange[] {
  return [...mentions].sort((a, b) => a.start - b.start);
}

/**
 * 送信時に使う mention binding 入力へ変換する。
 *
 * 失敗時:
 * - 現在の textarea 範囲へ収まらない mention は不正 state とみなし除外する。
 */
export function getAnalysisTargetMentionInputs(
  value: string,
  mentions: AnalysisTargetMentionInstance[],
): AnalysisScopeMentionInput[] {
  return getAnalysisTargetMentionTokenRanges(mentions)
    .filter(
      (mention) =>
        mention.start >= 0 &&
        mention.start < mention.end &&
        mention.end <= value.length,
    )
    .map((mention) => ({
      raw: value.slice(mention.start, mention.end),
      normalized: getAnalysisTargetMentionText(mention.target),
      refId: mention.refId,
    }));
}

/**
 * 本文中に現れる mention から、analysisScope に必要な target registry を復元する。
 */
export function getMentionedAnalysisTargets(
  mentions: AnalysisTargetMentionInstance[],
): AnalysisTarget[] {
  const seenRefIds = new Set<string>();
  const targets: AnalysisTarget[] = [];

  for (const mention of getAnalysisTargetMentionTokenRanges(mentions)) {
    if (seenRefIds.has(mention.refId)) {
      continue;
    }
    seenRefIds.add(mention.refId);
    targets.push(mention.target);
  }

  return targets;
}

/**
 * 特定 refId に紐づく inline mention を本文から全削除し、残存 mention の range を再計算する。
 *
 * 契約:
 * - 同じ refId を指す mention は本文内の全出現を一度に除去する。
 * - surviving mention は削除後の本文位置へ詰め直す。
 * - mention range が不正なものは fail-soft に無視する。
 */
export function removeAnalysisTargetMentionInstances(params: {
  value: string;
  mentions: AnalysisTargetMentionInstance[];
  refId: string;
}): {
  value: string;
  mentions: AnalysisTargetMentionInstance[];
} {
  const { value, mentions, refId } = params;
  const sortedMentions = getAnalysisTargetMentionTokenRanges(mentions);
  let nextValue = "";
  let nextCursor = 0;
  const nextMentions: AnalysisTargetMentionInstance[] = [];

  for (const mention of sortedMentions) {
    if (
      mention.start < nextCursor ||
      mention.start < 0 ||
      mention.end > value.length ||
      mention.start >= mention.end
    ) {
      continue;
    }

    nextValue += value.slice(nextCursor, mention.start);
    const mentionText = value.slice(mention.start, mention.end);

    if (mention.refId !== refId) {
      const nextStart = nextValue.length;
      nextValue += mentionText;
      nextMentions.push({
        ...mention,
        start: nextStart,
        end: nextStart + mentionText.length,
      });
    }

    nextCursor = mention.end;
  }

  nextValue += value.slice(nextCursor);

  return {
    value: nextValue,
    mentions: nextMentions,
  };
}

function getMentionTextChange(
  previousValue: string,
  nextValue: string,
): MentionTextChange {
  let prefixLength = 0;
  const maxPrefixLength = Math.min(previousValue.length, nextValue.length);
  while (
    prefixLength < maxPrefixLength &&
    previousValue[prefixLength] === nextValue[prefixLength]
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  const previousLength = previousValue.length;
  const nextLength = nextValue.length;
  while (
    previousLength - suffixLength > prefixLength &&
    nextLength - suffixLength > prefixLength &&
    previousValue[previousLength - suffixLength - 1] ===
      nextValue[nextLength - suffixLength - 1]
  ) {
    suffixLength += 1;
  }

  return {
    previousStart: prefixLength,
    previousEnd: previousLength - suffixLength,
    delta: nextLength - previousLength,
  };
}

function shouldRemoveMentionForChange(
  mention: AnalysisTargetMentionInstance,
  change: MentionTextChange,
): boolean {
  const isInsertionOnly = change.previousStart === change.previousEnd;
  if (isInsertionOnly) {
    return (
      change.previousStart > mention.start && change.previousStart < mention.end
    );
  }

  return (
    mention.start < change.previousEnd && mention.end > change.previousStart
  );
}

/**
 * textarea の通常編集結果に追従して mention ranges を更新する。
 *
 * 契約:
 * - 編集範囲の外にある mention は shift だけを行う。
 * - 編集範囲へ重なった mention は「壊れた」とみなし除去する。
 */
export function reconcileAnalysisTargetMentionInstances(params: {
  previousValue: string;
  nextValue: string;
  mentions: AnalysisTargetMentionInstance[];
}): AnalysisTargetMentionInstance[] {
  const { previousValue, nextValue, mentions } = params;
  if (previousValue === nextValue) {
    return getAnalysisTargetMentionTokenRanges(mentions);
  }

  const change = getMentionTextChange(previousValue, nextValue);

  return getAnalysisTargetMentionTokenRanges(
    mentions.flatMap((mention) => {
      if (shouldRemoveMentionForChange(mention, change)) {
        return [];
      }
      if (mention.end <= change.previousStart) {
        return [mention];
      }
      if (mention.start >= change.previousEnd) {
        return [
          {
            ...mention,
            start: mention.start + change.delta,
            end: mention.end + change.delta,
          },
        ];
      }
      return [];
    }),
  );
}

/**
 * 入力中の `@query` を確定メンションへ置換し、instance 配列も同時に更新する。
 *
 * 契約:
 * - textarea 上へ表示する文字列と `start/end` は常に同じ 1 回の置換結果から作る。
 * - 既存 mention の shift/remove もここでまとめて行い、呼び出し側での二重調整を避ける。
 */
export function insertAnalysisTargetMention(params: {
  value: string;
  mentions: AnalysisTargetMentionInstance[];
  match: {
    start: number;
    end: number;
  };
  target: AnalysisTarget;
}): {
  value: string;
  mentions: AnalysisTargetMentionInstance[];
  cursor: number;
} {
  const { value, mentions, match, target } = params;
  const before = value.slice(0, match.start);
  const after = value.slice(match.end);
  const mentionText = getAnalysisTargetMentionText(target);
  const replacement =
    after.length === 0 || !/^\s/.test(after) ? `${mentionText} ` : mentionText;
  const replaced = {
    text: `${before}${replacement}${after}`,
    cursor: before.length + replacement.length,
  };
  const shiftedMentions = reconcileAnalysisTargetMentionInstances({
    previousValue: value,
    nextValue: replaced.text,
    mentions,
  });
  const nextMention = createAnalysisTargetMentionInstance(
    target,
    match.start,
    match.start + mentionText.length,
  );

  return {
    value: replaced.text,
    mentions: getAnalysisTargetMentionTokenRanges([
      ...shiftedMentions,
      nextMention,
    ]),
    cursor: replaced.cursor,
  };
}

/**
 * メンション範囲を使って、表示用の plain/mention セグメントへ分割する。
 */
export function splitAnalysisTargetHighlightSegments(
  value: string,
  mentions: AnalysisTargetMentionInstance[],
): AnalysisTargetHighlightSegment[] {
  if (!value) {
    return [];
  }

  const mentionRanges = getAnalysisTargetMentionTokenRanges(mentions).filter(
    (range) =>
      range.start >= 0 && range.start < range.end && range.end <= value.length,
  );
  if (mentionRanges.length === 0) {
    return [{ kind: "plain", text: value }];
  }

  const segments: AnalysisTargetHighlightSegment[] = [];
  let cursor = 0;

  mentionRanges.forEach((range) => {
    if (range.start > cursor) {
      segments.push({
        kind: "plain",
        text: value.slice(cursor, range.start),
      });
    }

    segments.push({
      kind: "mention",
      text: value.slice(range.start, range.end),
      mention: range,
      target: range.target,
    });
    cursor = range.end;
  });

  if (cursor < value.length) {
    segments.push({
      kind: "plain",
      text: value.slice(cursor),
    });
  }

  return segments;
}

export type MentionTokenMoveDirection = "left" | "right";
type MentionTokenCursorDirection = "backward" | "forward";

function findMentionTokenRangeAtCursor(
  cursor: number,
  ranges: AnalysisTargetMentionTokenRange[],
  direction: MentionTokenCursorDirection,
): AnalysisTargetMentionTokenRange | undefined {
  return ranges.find((range) =>
    direction === "backward"
      ? cursor > range.start && cursor <= range.end
      : cursor >= range.start && cursor < range.end,
  );
}

/**
 * 指定カーソルが既存メンショントークン内部にあるか判定する。
 */
export function isCursorInsideMentionToken(
  cursor: number,
  ranges: AnalysisTargetMentionTokenRange[],
): boolean {
  return (
    findMentionTokenRangeAtCursor(cursor, ranges, "backward") !== undefined
  );
}

/**
 * キャレットがメンション内部にいる場合、1 トークン単位で移動先を返す。
 *
 * `null` は通常の文字単位移動に委譲してよいことを示す。
 */
export function getMentionTokenCursorMoveTarget(
  cursor: number,
  ranges: AnalysisTargetMentionTokenRange[],
  direction: MentionTokenMoveDirection,
): number | null {
  const tokenRange = findMentionTokenRangeAtCursor(
    cursor,
    ranges,
    direction === "left" ? "backward" : "forward",
  );

  if (!tokenRange) {
    return null;
  }

  return direction === "left" ? tokenRange.start : tokenRange.end;
}

export type MentionTokenDeleteDirection = "backward" | "forward";

/**
 * Backspace/Delete 押下時に、メンションを 1 塊で削除すべき範囲を返す。
 *
 * 先頭/末尾の境界で削除した場合は後続スペース 1 文字も含めて返し、
 * トークン削除後に不要な空白が残りづらい状態を保つ。
 */
export function getMentionTokenDeletionRange(
  value: string,
  cursor: number,
  ranges: AnalysisTargetMentionTokenRange[],
  direction: MentionTokenDeleteDirection,
): { start: number; end: number } | null {
  const tokenRange = findMentionTokenRangeAtCursor(cursor, ranges, direction);

  if (!tokenRange) {
    return null;
  }

  const nextChar = value[tokenRange.end];
  const shouldDeleteTrailingSpace = nextChar === " ";

  return {
    start: tokenRange.start,
    end: shouldDeleteTrailingSpace ? tokenRange.end + 1 : tokenRange.end,
  };
}

/**
 * 選択範囲がメンショントークンに重なる場合、削除範囲をトークン境界へ拡張する。
 *
 * 契約:
 * - 非 collapsed 選択のみ対象。
 * - 選択範囲が 1 つ以上のトークンへ重なれば、該当トークンは必ず全体削除する。
 * - トークン末尾直後が半角スペースの場合は 1 文字追加で削除し、不要な空白残りを抑える。
 *
 * 失敗時:
 * - トークン非重複なら `null` を返し、通常の選択削除へ委譲する。
 */
export function getMentionTokenSelectionDeletionRange(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  ranges: AnalysisTargetMentionTokenRange[],
): { start: number; end: number } | null {
  if (selectionStart === selectionEnd) {
    return null;
  }

  const normalizedStart = Math.min(selectionStart, selectionEnd);
  const normalizedEnd = Math.max(selectionStart, selectionEnd);
  const overlappedRanges = ranges.filter(
    (range) => range.start < normalizedEnd && range.end > normalizedStart,
  );
  if (overlappedRanges.length === 0) {
    return null;
  }

  const firstOverlappedRange = overlappedRanges[0];
  const lastOverlappedRange = overlappedRanges[overlappedRanges.length - 1];
  const start = Math.min(normalizedStart, firstOverlappedRange?.start ?? 0);
  let end = Math.max(normalizedEnd, lastOverlappedRange?.end ?? normalizedEnd);

  const shouldDeleteTrailingSpace =
    end === lastOverlappedRange?.end && value[end] === " ";
  if (shouldDeleteTrailingSpace) {
    end += 1;
  }

  return { start, end };
}
